import { useMutation, useQueryClient } from "@tanstack/react-query";
import { signInWithEmailAndPassword } from "firebase/auth";

import { auth, forceInMemorySession } from "@/lib/firebase/firebase-client";

import { fetchJson } from "@/dashboard/hooks/api/fetch-json";

interface SignInArgs {
  email: string;
  password: string;
  googleIdpTenantId: string;
}

export function useSignIn() {
  const qc = useQueryClient();

  return useMutation<void, Error, SignInArgs>({
    mutationFn: async ({ email, password, googleIdpTenantId }) => {
      // Ensure correct tenant
      const previousTenant = auth.tenantId;
      auth.tenantId = googleIdpTenantId;

      try {
        // Memory-only persistence
        await forceInMemorySession();

        // Firebase sign-in
        const { user } = await signInWithEmailAndPassword(auth, email, password);
        const idToken = await user.getIdToken();

        // Tell the server to mint/refresh the session cookie
        await fetchJson("/api/dashboard/session", {
          method: "POST",
          body: JSON.stringify({ idToken }),
        });
      } finally {
        // Restore previous tenant
        auth.tenantId = previousTenant;
      }
    },
    onSuccess: async () => {
      // Guarantees AuthProvider sees fresh user immediately
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
